import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';
import { ApiService } from '../api/api.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { StateService } from '../state/state.service';
import { PaymentService } from '../payment/payment.service';

interface Role {
  name: string;
  permissions: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private rolesSubject = new BehaviorSubject<Role[]>([]);
  private currentRoleSubject = new BehaviorSubject<string | null>(null); // Current role
  private baseUrl = environment.apiUrl;
  // private baseUrl = 'https://mobile-gateway.dev-api.knostos.com/api/v1';
  private headers = new HttpHeaders({
    //medlink
    // 'app-id': 'nlSwUjIBL3QpB8++8OWCog==',
    //ultimated
    'app-id': 'nlSwUjIBL3QpB8++8OWCog==',
  });
  bridgepayApiPciApiKey: string = '';
  user: any;

  constructor(
    private http: HttpClient,
    private stateService: StateService,
    private bridgepayService: PaymentService
  ) {
    const savedRoles = localStorage.getItem('userRoles');
    const savedRole = localStorage.getItem('currentRole');
    this.user = this.stateService.getUserInfo();
    if (savedRoles) {
      this.rolesSubject.next(JSON.parse(savedRoles ?? null));
    }

    if (savedRole) {
      this.currentRoleSubject.next(savedRole);
    }

    const storedLang = localStorage.getItem('lang') ?? 'en';
    localStorage.setItem('lang', storedLang);
  }

  loadBridgePayConfig(): void {
    this.user = this.stateService.getUserInfo();
    if (!this.user.company_uuid || this.user.company_uuid === '') return;
    this.bridgepayService
      .getDefaultOrganization(this.user.company_uuid)
      .subscribe(
        (configs) => {
          this.bridgepayApiPciApiKey = configs.data.bridgepay_pci_api_key;
        },
        (error) => {
          console.error('Error fetching payment configurations:', error);
        }
      );
    if (!this.user?.company_uuid) {
      console.warn(
        'Tenant ID is not available. Cannot fetch BridgePay config.'
      );
    }
  }

  setRoles(roles: Role[]): void {
    this.rolesSubject.next(roles);
    localStorage.setItem('userRoles', JSON.stringify(roles));
  }

  getRoles(): Role[] {
    return this.rolesSubject.value;
  }

  setCurrentRole(role: string): void {
    this.currentRoleSubject.next(role);
    localStorage.setItem('currentRole', role);
  }

  isLoggedin(): Observable<boolean> {
    return this.validateSession();
  }

  getCurrentRole(): string | null {
    return this.isLoggedin() ? this.currentRoleSubject.value : null;
  }

  /**
   * Resets the AuthService state completely
   */
  reset(): void {
    this.rolesSubject.next([]);
    this.currentRoleSubject.next(null);
    localStorage.removeItem('userRoles');
    localStorage.removeItem('currentRole');
    localStorage.removeItem('userInfo');
    this.stateService.clearUser();
  }

  /**
   * Logs the user out and resets the application state.
   * Returns an observable to notify when logout is completed.
   */
  logout(): Observable<void> {
    this.reset(); // Clear local state

    return this.http
      .post<void>(
        `${this.baseUrl}/logout`,
        {},
        { headers: this.headers, withCredentials: true }
      )
      .pipe(
        map(() => {
          console.log('Successfully logged out');
        }),
        take(1),
        // tap(() => window.location.reload()),
        catchError((error) => {
          console.error('Error during logout:', error);
          return of(); // Gracefully handle errors
        })
      );
  }

  /**
   * Ends the server session but keeps local state, so the current screen can
   * still render. Call `stateService.clearUser()` when leaving it.
   */
  endServerSession(): Observable<void> {
    return this.http
      .post<void>(
        `${this.baseUrl}/logout`,
        {},
        { headers: this.headers, withCredentials: true }
      )
      .pipe(take(1));
  }

  /**
   * Validates the session with the server.
   * Returns true if session is valid, false otherwise.
   */
  validateSession(): Observable<boolean> {
    return this.http
      .get(`${this.baseUrl}/check_session`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        map(() => true),
        catchError(() => of(false)) // Treat errors as invalid session
      );
  }
}
